import React, { memo } from 'react';
import { Capacitor } from '@capacitor/core';
import { useCustomer } from '@olo-web/domain/customer';
import { useCustomerState } from '@olo-web/client-state';
import { useIsClient } from '@olo-web/utils/common/hooks/useIsClient';
import { LoginButton } from '@olo-web/components/atoms/LoginButton';
import {
  SkeletonCircle,
  ButtonProps,
  AvatarProps,
  IconProps,
  MenuButtonProps,
} from '@chakra-ui/react';
import { CustomerAvatarMenu } from '../CustomerAvatarMenu';
import { DisruptionIconButton } from '@olo-web/components/molecules/DisruptionIconButton/DisruptionIconButton';

type TCustomerOrLogin = {
  buttonProps?: ButtonProps;
  buttonIconProps?: IconProps;
  avatarProps?: AvatarProps;
  avatarMenuButtonProps?: MenuButtonProps & { 'data-testid'?: string };
};

const CustomerOrLoginComponent = ({
  buttonProps = {},
  buttonIconProps = {},
  avatarProps = {},
  avatarMenuButtonProps = {},
}: TCustomerOrLogin) => {
  const { data: customer, isLoading } = useCustomer();
  const { isAuthHydrated } = useCustomerState();
  const isClient = useIsClient();

  // On native, wait for the async secure-storage rehydration before deciding
  // logged-in vs logged-out, so we don't flash the login button on launch.
  // Gated on `isClient` so the server render and the first client render match
  // (both take the non-native path) and we don't cause a hydration mismatch.
  // Web is unaffected (rehydration resolves synchronously as "not native").
  const isWaitingForNativeHydration = isClient && Capacitor.isNativePlatform() && !isAuthHydrated;

  if (isLoading || isWaitingForNativeHydration) {
    return <SkeletonCircle zIndex="3" />;
  }

  if (!customer?.customerId)
    return (
      <>
        <DisruptionIconButton display={{ base: 'inline', md: 'none' }} />
        <LoginButton {...buttonProps} iconProps={buttonIconProps} zIndex="3" />
      </>
    );

  return (
    <CustomerAvatarMenu avatarProps={avatarProps} avatarMenuButtonProps={avatarMenuButtonProps} />
  );
};

export const CustomerOrLogin = memo(CustomerOrLoginComponent);
